import { Stack } from '@understory-io/leaf/stack';
import { cx } from '~/styled-system/css';
import { css } from '@understory-io/leaf/css';
import { maxWidthContent } from './shared';
import React, { Children, isValidElement } from 'react';

/**
 * <Page> wraps entire pages and sets the base surface
 * and forces a 100% screen height size.
 *
 * Overlays need no portal wiring here: the `Leaf` provider hands react-aria its
 * themed container, so anything portaling out still lands inside the theme.
 * That only holds while react-aria stays a single module instance — see the
 * `optimizeDeps.include` note in `vite.config.ts`.
 */
export function Page({ children }: { children?: React.ReactNode }) {
  return (
    <Stack
      className={css({
        minHeight: 'svh',
        background: 'surface.base',
        color: 'content.primary',
      })}
    >
      {children}
    </Stack>
  );
}

/**
 * <PageCard> is a responsive container for content on most pages.
 * The PageCard has a raised surface color and some defaults for padding and width.
 */
export function PageCard({ children }: { children: React.ReactNode }) {
  return (
    <div
      className={cx(
        css({
          background: { base: 'surface.base' },
          borderBottomRadius: 'xlarge',
          paddingTop: { base: 'none', tablet: '2xlarge' },
          paddingBottom: { base: '3xlarge', tablet: '[200px]' },
          flex: '1',
          px: { base: 'small', tablet: 'large' },
        })
      )}
    >
      <div className={maxWidthContent}>{children}</div>
    </div>
  );
}

/**
 * <TwoColumnLayout> is used inside a <PageCard>.
 * It renders it's children (only two) in a grid side by side.
 * Any more elements than two are dropped.
 */
export function TwoColumnLayout({ children }: { children: React.ReactNode }) {
  const childNodes = Children.toArray(children);
  const [first, second] = childNodes;
  if (!isValidElement(first) || !isValidElement(second)) {
    throw new Error('TwoColumnLayout expectes exactly two children');
  }

  return (
    <div
      className={css({
        display: { base: 'flex', tablet: 'grid' },
        gridTemplateColumns: { base: '1fr', tablet: '[1fr 360px]' },
        gridTemplateAreas: {
          base: '"main"',
          tablet: '"main aside"',
        },
        gap: { base: 'large', tablet: 'xlarge' },
        alignItems: 'start',
      })}
    >
      <div
        className={css({
          gridArea: 'main',
        })}
      >
        {first}
      </div>
      <aside
        className={css({
          width: 'full',
          gridArea: 'aside',
          display: { base: 'contents', tablet: 'block' },

          alignSelf: { tablet: 'stretch' },
        })}
      >
        {second}
      </aside>
    </div>
  );
}
